import FilterSidebar from '../components/layout/FilterSidebar';
import { useTheme } from '../context/ThemeContext';
import { CatalogEmptyState } from '../features/catalog/components/CatalogEmptyState';
import { CatalogGrid } from '../features/catalog/components/CatalogGrid';
import { CatalogPagination } from '../features/catalog/components/CatalogPagination';
import { CatalogToolbar } from '../features/catalog/components/CatalogToolbar';
import { useCatalogPagination } from '../features/catalog/useCatalogPagination';
import { useCatalogState } from '../features/catalog/useCatalogState';

export default function Catalog() {
  const { language } = useTheme();
  const {
    brandFromUrl,
    sortBy,
    setSortBy,
    filters,
    activeCategories,
    filteredProducts,
    pagination,
    maxCatalogPrice,
    title,
    hasBrandEmptyState,
    resolvedBrandLabel,
    productsQuery,
    handleFilterChange,
    resetPaginationForFilterChange,
    handleUserPriceChange,
    setActiveCategory,
  } = useCatalogState(language);
  const { currentPage, paginationItems, handlePageChange } = useCatalogPagination(pagination?.totalPages);
  const loadError =
    productsQuery.error instanceof Error ? productsQuery.error.message : productsQuery.error ? 'Failed to load products.' : null;
  const isLoading = productsQuery.isLoading;

  return (
    <div className="min-h-screen bg-[var(--ubuy-page-bg)] text-[var(--ubuy-text)]">
      <div className="mx-auto max-w-[1375px] px-[25px] py-8 pt-28">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
          <FilterSidebar
            onFilterChange={handleFilterChange}
            onUserFilterChange={resetPaginationForFilterChange}
            onUserPriceChange={handleUserPriceChange}
            onCategoryChange={setActiveCategory}
            maxPrice={maxCatalogPrice}
            activeCategories={activeCategories}
          />

          <div className="min-w-0 flex-1">
            <CatalogToolbar
              language={language}
              title={title}
              count={pagination?.totalCount ?? filteredProducts.length}
              isLoading={isLoading}
              loadError={loadError}
              sortBy={sortBy}
              onSortChange={setSortBy}
            />

            {isLoading ? (
              <div data-testid="products-loading" className="grid animate-pulse grid-cols-2 gap-4 lg:grid-cols-3 xl:grid-cols-4">
                {[...Array(8)].map((_, i) => (
                  <div key={i} className="h-[320px] rounded-[15px] border border-[var(--ubuy-border-soft)] bg-white" />
                ))}
              </div>
            ) : loadError ? (
              <div data-testid="products-error" className="rounded-[15px] border border-[var(--ubuy-border-soft)] bg-white px-6 py-12 text-center">
                <p className="mb-4 text-sm font-medium text-[var(--ubuy-danger)]">{loadError}</p>
                <button
                  data-testid="retry-products-btn"
                  onClick={() => window.location.reload()}
                  className="rounded-full bg-[var(--ubuy-title)] px-6 py-2 text-sm font-bold text-white transition hover:bg-[var(--ubuy-accent)] hover:text-[var(--ubuy-title)]"
                >
                  {language === 'mn' ? 'Дахин оролдох' : 'Retry'}
                </button>
              </div>
            ) : hasBrandEmptyState ? (
              <CatalogEmptyState language={language} resolvedBrandLabel={resolvedBrandLabel} />
            ) : (
              <div className="space-y-8">
                <CatalogGrid products={filteredProducts} showBrandBadge={Boolean(brandFromUrl)} />

                {pagination && pagination.totalPages && pagination.totalPages > 1 ? (
                  <CatalogPagination
                    language={language}
                    currentPage={currentPage}
                    paginationItems={paginationItems}
                    hasPrevPage={pagination.hasPrevPage}
                    hasNextPage={pagination.hasNextPage}
                    onPageChange={handlePageChange}
                  />
                ) : null}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
